import FreeIcon from "../../../resources/icons/free.svg";
import SvodIcon from "../../../resources/icons/svod.svg";
import TvodIcon from "../../../resources/icons/tvod.svg";
import {
  isMobilePlatform,
  PLATFORM_TYPES,
  platformSupportsGeoblock,
} from "../offerPlan.constants";

import type { GeoblockMode } from "../../../api/geoblock.types";
import type { ProviderPlan } from "../../../api/OfferAPI.types";
import type {
  GetPlatformsResponse,
  OfferPlanProvider,
} from "../../../api/ProjectAPI.types";
import type { OfferPlanTypeValue } from "../../../models/OfferPlan.types";
import type { PurchaseType } from "../../../types";

type CurrencyCode =
  | "EUR"
  | "USD"
  | "GBP"
  | "JPY"
  | "CHF"
  | "TRY"
  | "BRL"
  | "RUB"
  | "CAD"
  | "ILS"
  | "KRW"
  | "ARS"
  | "NOK";

export const currencyMin: Record<CurrencyCode, number> = {
  EUR: 0.5,
  USD: 0.5,
  GBP: 0.3,
  JPY: 50,
  CHF: 0.5,
  TRY: 4.2,
  BRL: 0.5,
  RUB: 45,
  CAD: 0.5,
  ILS: 0.5,
  KRW: 50,
  ARS: 100,
  NOK: 3,
};

export const CURRENCY_OPTION_LIST: {
  value: string;
  label: string;
  symbol: string;
}[] = [
  { value: "EUR", label: "Euro", symbol: "€" },
  { value: "USD", label: "US Dollar", symbol: "$" },
  { value: "GBP", label: "British Pound", symbol: "£" },
  { value: "JPY", label: "Japanese Yen", symbol: "¥" },
  { value: "CHF", label: "CHF", symbol: "CHF" },
  { value: "TRY", label: "TRY", symbol: "TRY" },
  { value: "BRL", label: "BRL", symbol: "BRL" },
  { value: "RUB", label: "RUB", symbol: "RUB" },
  { value: "CAD", label: "CAD", symbol: "CAD" },
  { value: "ILS", label: "ILS", symbol: "ILS" },
  { value: "KRW", label: "KRW", symbol: "KRW" },
  { value: "ARS", label: "ARS", symbol: "ARS" },
  { value: "NOK", label: "NOK", symbol: "NOK" },
];

export const FREQUENCY_OPTION_LIST = [1, 3, 6, 9, 12];
export const TRIAL_PRESET_LIST = [0, 7, 14];
export const RENTAL_PRESET_LIST = [7, 14, 30];

/**
 * Structural subset of a plan (raw `OfferPlanType` or `PlanFormValues`) that
 * `formStatus`/`getPlanFieldState` need to compute field visibility/locking.
 * `subscriptions`/`purchases` are absent from `PlanFormValues` — being
 * optional here, that's treated the same as "no clients yet".
 */
export interface PlanFieldStateInput {
  platform: string;
  type: OfferPlanTypeValue;
  isNew?: boolean;
  monetization_enabled?: boolean;
  geoblock_mode?: GeoblockMode;
  subscriptions?: PurchaseType;
  purchases?: PurchaseType;
}

export const formStatus = (
  plan: PlanFieldStateInput,
): {
  platformSelectorDisabled: boolean;
  skuDisabled: boolean;
  operatorDcbDisabled: boolean;
  currencySelectorDisabled: boolean;
  priceSelectorDisabled: boolean;
  donationDisabled: boolean;
  giftDisabled: boolean;
  geoblockSelectorDisabled: boolean;
  geoRestrictSelectorDisabled: boolean;
  frequencySelectorDisabled: boolean;
  trialDurationSelectorDisabled: boolean;
  isFreeType: boolean;
  typeAccessDurationSelectorDisabled: boolean;
  isTVODType: boolean;
} => {
  const isOkastPlatform = plan.platform === "okast";
  const hasPlatformGeoblock = platformSupportsGeoblock(plan.platform);
  const isMobile = isMobilePlatform(plan.platform);
  const isRetailerPlatform = plan.platform === "retailer";
  const isFreeType = plan.type === "free";
  const isTVODType = plan.type === "tvod_bundle";
  const isReadOnly =
    !plan.isNew ||
    (plan.subscriptions?.total ?? 0) > 0 ||
    (plan.purchases?.total ?? 0) > 0;

  const platformSelectorDisabled = isReadOnly;
  const skuDisabled = !isMobile || isReadOnly;
  const operatorDcbDisabled = !isRetailerPlatform;
  const currencySelectorDisabled = !isOkastPlatform || isReadOnly;
  const priceSelectorDisabled = !isOkastPlatform || isReadOnly;
  const donationDisabled = !isOkastPlatform;
  const giftDisabled = !isOkastPlatform;
  const geoblockSelectorDisabled = !hasPlatformGeoblock && !isFreeType;
  const geoRestrictSelectorDisabled =
    (!hasPlatformGeoblock && !plan.geoblock_mode) ||
    plan.geoblock_mode === "disabled";
  const frequencySelectorDisabled = isReadOnly;
  const trialDurationSelectorDisabled = !isOkastPlatform || isReadOnly;
  const typeAccessDurationSelectorDisabled = isReadOnly;

  return {
    platformSelectorDisabled,
    skuDisabled,
    operatorDcbDisabled,
    currencySelectorDisabled,
    priceSelectorDisabled,
    donationDisabled,
    giftDisabled,
    geoblockSelectorDisabled,
    geoRestrictSelectorDisabled,
    frequencySelectorDisabled,
    trialDurationSelectorDisabled,
    isFreeType,
    typeAccessDurationSelectorDisabled,
    isTVODType,
  };
};

export const PLAN_READONLY_REASON_KEY = "readOnlyLockNote";
export const PLAN_D2G_AVOD_LOCK_REASON_KEY = "downloadToGoDisabled";

export type PlanFieldKey =
  | "enabled"
  | "platform"
  | "provider_plans"
  | "platform_product_id"
  | "storefront_retailer_uuid"
  | "pricing"
  | "currency"
  | "price"
  | "accessDuration"
  | "frequency"
  | "trial_duration"
  | "resolution"
  | "d2g_enabled"
  | "donation_enabled"
  | "gift_enabled"
  | "monetization"
  | "require_registration"
  | "geoblock_mode"
  | "countries";

export interface PlanFieldState {
  hidden: boolean;
  disabled: boolean;
  reasonKey?: string;
}

export type PlanFieldStateMap = Record<PlanFieldKey, PlanFieldState>;

export const getPlanFieldState = (
  plan: PlanFieldStateInput,
): PlanFieldStateMap => {
  const status = formStatus(plan);
  const { OKAST, RETAILER, EDU } = PLATFORM_TYPES;

  const isOkast = plan.platform === OKAST;
  const isEdu = plan.platform === EDU;
  const isMobile = isMobilePlatform(plan.platform);
  const isRetailer = plan.platform === RETAILER;
  const isFree = plan.type === "free";
  const isSvod = plan.type === "svod";
  const isPaid = !isFree;

  const pricingHidden = isFree || !isOkast;
  const lock = (
    hidden: boolean,
    disabled: boolean,
    reasonKey: string = PLAN_READONLY_REASON_KEY,
  ): PlanFieldState => ({
    hidden,
    disabled,
    reasonKey: disabled ? reasonKey : undefined,
  });

  return {
    enabled: { hidden: isEdu, disabled: false },
    platform: lock(isFree || isEdu, status.platformSelectorDisabled),
    provider_plans: {
      hidden: !(isOkast && isPaid),
      disabled: false,
    },
    platform_product_id: lock(!isMobile, status.skuDisabled),
    storefront_retailer_uuid: {
      hidden: !isRetailer,
      disabled: status.operatorDcbDisabled,
    },
    pricing: { hidden: pricingHidden, disabled: false },
    currency: lock(pricingHidden, status.currencySelectorDisabled),
    price: lock(pricingHidden, status.priceSelectorDisabled),
    accessDuration: lock(
      pricingHidden || isSvod,
      status.typeAccessDurationSelectorDisabled,
    ),
    frequency: lock(pricingHidden || !isSvod, status.frequencySelectorDisabled),
    trial_duration: lock(
      !isSvod || !isOkast,
      status.trialDurationSelectorDisabled,
    ),
    resolution: { hidden: false, disabled: false },
    d2g_enabled: lock(
      false,
      Boolean(plan.monetization_enabled),
      PLAN_D2G_AVOD_LOCK_REASON_KEY,
    ),
    donation_enabled: {
      hidden: isFree || isEdu,
      disabled: status.donationDisabled,
    },
    gift_enabled: { hidden: isFree || isEdu, disabled: status.giftDisabled },
    monetization: { hidden: false, disabled: false },
    require_registration: { hidden: isPaid, disabled: false },
    geoblock_mode: { hidden: false, disabled: status.geoblockSelectorDisabled },
    countries: { hidden: false, disabled: status.geoRestrictSelectorDisabled },
  };
};

export interface Retailer {
  label: string;
  value: string;
  uuid?: string;
}

export interface AvailablePlatform {
  okast?: boolean;
  apple?: boolean;
  google?: boolean;
  retailer?: boolean;
  free?: boolean;
}

export const getAvailableOfferPlanProvider = (
  offerPlanProviderList: OfferPlanProvider[],
): ProviderPlan[] => {
  const hasPaypalProvider = offerPlanProviderList?.some(
    (offerPlanProvider) => offerPlanProvider.payment_provider === "paypal",
  );

  const hasStripeProvider = offerPlanProviderList?.some(
    (offerPlanProvider) => offerPlanProvider.payment_provider === "stripe",
  );

  const currentProviderPlans: ProviderPlan[] = [];
  if (hasPaypalProvider) currentProviderPlans.push("paypal");
  if (hasStripeProvider) currentProviderPlans.push("stripe");
  return currentProviderPlans;
};

export const getAvailablePlatforms = (
  offerType: OfferPlanTypeValue,
  platforms?: GetPlatformsResponse,
): AvailablePlatform => {
  const hasPaymentProviders = platforms && platforms?.providers.length > 0;
  const hasRetailers = platforms && platforms?.retailers.length > 0;
  const hasIOSApp =
    platforms && platforms?.applications?.findIndex((appli) => appli.ios) > -1;
  const hasAndroidApp =
    platforms &&
    platforms?.applications?.findIndex((appli) => appli.android) > -1;

  return {
    okast: hasPaymentProviders,
    retailer: hasRetailers,
    apple: hasIOSApp,
    google: hasAndroidApp,
    free: offerType === "free",
  };
};

export const getOfferPlanTypeIcon = (planType: OfferPlanTypeValue): string => {
  switch (planType) {
    case "tvod_bundle":
      return TvodIcon as string;
    case "svod":
      return SvodIcon as string;
    case "free":
      return FreeIcon as string;
    default:
      return "";
  }
};
